import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Download, Inbox as InboxIcon, MessageCircle, BellRing, Upload } from "lucide-react";
import { enablePush, pushSupported } from "@/lib/push";
import { toast } from "sonner";

const statusStyle = (s) =>
  s === "confirmed" ? "bg-brandgreen/10 text-brandgreen"
    : s === "cancelled" ? "bg-red-100 text-red-600"
    : s === "paid" ? "bg-teal/10 text-teal"
    : "bg-amber-100 text-amber-700";

function Countdown({ deadline }) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(deadline).getTime() - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, new Date(deadline).getTime() - Date.now())), 1000);
    return () => clearInterval(t);
  }, [deadline]);
  if (left <= 0) return <p className="text-red-600 font-bold text-sm" data-testid="timer-expired">Payment window expired — the seat has been released.</p>;
  const h = Math.floor(left / 3600000), m = Math.floor((left % 3600000) / 60000), s = Math.floor((left % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return <p className="text-red-600 font-extrabold text-3xl tabular-nums tracking-tight" data-testid="payment-timer">{pad(h)}:{pad(m)}:{pad(s)}</p>;
}

export default function Inbox() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/my/messages").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  const turnOnPush = async () => {
    try {
      const ok = await enablePush();
      toast[ok ? "success" : "info"](ok ? "Notifications enabled!" : "You can enable them later from your browser.");
    } catch (e) { toast.error(e.message || "Could not enable notifications"); }
  };

  const uploadReceipt = async (id, e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post(`/my/bookings/${id}/receipt`, fd);
      toast.success("Receipt uploaded — Coach Reyland will confirm your seat.");
      const r = await api.get("/my/messages"); setItems(r.data);
    } catch { toast.error("Upload failed, please try again"); }
  };

  if (!items) return <Loader />;
  const fileHref = (u) => (u && u.startsWith("http") ? u : process.env.REACT_APP_BACKEND_URL + u);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="inbox-page">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold">My Messages</h1>
          <p className="text-muted-foreground text-sm mt-1">Replies and files from Coach Reyland land here.</p>
        </div>
        {pushSupported() && (
          <Button variant="outline" className="rounded-full" onClick={turnOnPush} data-testid="inbox-enable-push">
            <BellRing className="w-4 h-4 mr-1.5" /> Enable notifications
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground bg-white border border-border rounded-2xl" data-testid="inbox-empty">
          <InboxIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
          No messages yet. When Coach Reyland replies to your booking, it'll show up here.
        </div>
      ) : (
        items.map((b) => (
          <div key={b.id} className="bg-white border border-border rounded-2xl p-5 shadow-sm mb-4" data-testid={`inbox-booking-${b.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-heading font-bold">{b.session_type === "group" ? "Group" : "1-on-1"} Mentorship</p>
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${statusStyle(b.status)}`}>{b.status}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{b.preferred_time}</p>
            {b.status === "pending" && b.payment_deadline && !b.receipt_uploaded && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4" data-testid={`payment-box-${b.id}`}>
                <Countdown deadline={b.payment_deadline} />
                <p className="text-sm text-red-700 mt-1 font-medium">Complete your payment before the timer runs out to secure your seat.</p>
                <label className="inline-flex items-center gap-1.5 mt-3 bg-teal text-white rounded-full px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-teal/90" data-testid={`upload-receipt-${b.id}`}>
                  <Upload className="w-4 h-4" /> Upload payment receipt
                  <input type="file" className="hidden" onChange={(e) => uploadReceipt(b.id, e)} />
                </label>
              </div>
            )}
            {b.receipt_uploaded && b.status !== "cancelled" && (
              <p className="text-sm text-brandgreen mt-3 font-medium" data-testid={`receipt-done-${b.id}`}>✓ Payment receipt uploaded — awaiting confirmation.</p>
            )}
            <div className="mt-4 space-y-3">
              {(b.replies || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No replies yet — Coach Reyland will be in touch soon.</p>
              ) : (
                (b.replies || []).map((r, i) => (
                  <div key={i} className="bg-muted/40 rounded-xl p-3" data-testid={`inbox-reply-${b.id}-${i}`}>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <MessageCircle className="w-3.5 h-3.5 text-teal" /> {r.by} · {r.at ? new Date(r.at).toLocaleString() : ""}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                    {r.file_url && (
                      <a href={fileHref(r.file_url)} target="_blank" rel="noreferrer" download>
                        <Button size="sm" className="rounded-full bg-teal hover:bg-teal/90 mt-2" data-testid={`inbox-download-${b.id}-${i}`}>
                          <Download className="w-4 h-4 mr-1.5" /> Download {r.file_name || "file"}
                        </Button>
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
